<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ LOADING ══ -->
    <template v-if="loading">

      <!-- Mobile skeleton -->
      <div class="sm:hidden animate-pulse bg-gray-50 min-h-screen">
        <!-- Header -->
        <div class="bg-white border-b border-gray-100 px-4 flex items-center justify-between h-14">
          <div class="h-6 bg-gray-200 rounded-lg w-36"></div>
          <div class="h-8 bg-gray-200 rounded-full w-16"></div>
        </div>
        <div class="px-4 pb-8">
          <!-- Weekly schedule -->
          <div class="h-3 bg-gray-200 rounded w-32 mt-6 mb-2"></div>
          <div class="bg-white rounded-2xl overflow-hidden divide-y divide-gray-100">
            <div v-for="i in 7" :key="i" class="flex items-center px-4 py-3.5 gap-3">
              <div class="h-4 bg-gray-200 rounded w-24 flex-shrink-0"></div>
              <div class="h-3 bg-gray-100 rounded flex-1"></div>
              <div class="h-6 w-11 bg-gray-200 rounded-full flex-shrink-0"></div>
            </div>
          </div>
          <!-- Default hours -->
          <div class="h-3 bg-gray-200 rounded w-28 mt-5 mb-2"></div>
          <div class="bg-white rounded-2xl px-4 py-3.5 flex items-center gap-3">
            <div class="h-5 w-5 bg-gray-200 rounded flex-shrink-0"></div>
            <div class="h-9 bg-gray-100 rounded-xl flex-1"></div>
            <div class="h-3 bg-gray-200 rounded w-3"></div>
            <div class="h-9 bg-gray-100 rounded-xl flex-1"></div>
          </div>
          <!-- Date overrides -->
          <div class="h-3 bg-gray-200 rounded w-28 mt-5 mb-2"></div>
          <div class="bg-white rounded-2xl overflow-hidden">
            <div class="px-4 pt-3 pb-1">
              <div class="h-3 bg-gray-100 rounded w-48"></div>
            </div>
            <div class="grid grid-cols-7 gap-0 p-2">
              <div v-for="i in 35" :key="i" class="flex justify-center py-2">
                <div class="w-8 h-8 bg-gray-100 rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop skeleton — matches h-screen two-panel layout exactly -->
      <div class="hidden sm:flex sm:flex-col sm:h-screen animate-pulse">

        <!-- Header -->
        <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-6 py-3.5 flex items-center justify-between">
          <div class="space-y-1.5">
            <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-52"></div>
          </div>
          <div class="h-9 bg-gray-200 dark:bg-gray-700 rounded-lg w-32"></div>
        </div>

        <!-- Body -->
        <div class="flex-1 flex overflow-hidden">

          <!-- Left panel skeleton -->
          <div class="w-72 xl:w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] p-5 space-y-6">
            <!-- Days label + pills -->
            <div class="space-y-3">
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
              <div class="grid grid-cols-7 gap-1">
                <div v-for="i in 7" :key="i" class="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>
            <!-- Divider -->
            <div class="h-px bg-gray-100 dark:bg-gray-700"></div>
            <!-- Hours label + selects -->
            <div class="space-y-3">
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
              <div class="flex items-center gap-2">
                <div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex-1"></div>
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4"></div>
                <div class="h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex-1"></div>
              </div>
            </div>
          </div>

          <!-- Right panel skeleton — calendar grid -->
          <div class="flex-1 bg-gray-50 dark:bg-[#0d1117] overflow-hidden">
            <!-- Month nav bar -->
            <div class="bg-gray-100 dark:bg-gray-800 px-6 py-3 flex items-center justify-between">
              <div class="h-4 bg-gray-300 dark:bg-gray-700 rounded w-8"></div>
              <div class="h-4 bg-gray-300 dark:bg-gray-700 rounded w-28"></div>
              <div class="h-4 bg-gray-300 dark:bg-gray-700 rounded w-8"></div>
            </div>
            <!-- Weekday header -->
            <div class="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700">
              <div v-for="i in 7" :key="i"
                class="py-3 border-r border-gray-200 dark:border-gray-700 last:border-r-0">
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-8 mx-auto"></div>
              </div>
            </div>
            <!-- Day cells — 5 rows × 7 cols -->
            <div class="grid grid-cols-7">
              <div v-for="i in 35" :key="i"
                class="border-b border-r border-gray-200 dark:border-gray-700 last:border-r-0 p-2 space-y-1.5"
                :class="i > 28 ? 'border-b-0' : ''">
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5"></div>
                <div v-if="i % 3 === 0" class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
              </div>
            </div>
          </div>

        </div>
      </div>

    </template>

    <!-- ══ ERROR ══ -->
    <template v-else-if="error">
      <div class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="() => { availabilityStore.invalidate(); fetchCalendarData() }"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm">
          Try again
        </button>
      </div>
    </template>

    <!-- ══ LOADED ══ -->
    <template v-else>

      <!-- ── MOBILE LAYOUT ── -->
      <div class="sm:hidden bg-gray-50 min-h-screen">

        <!-- Sticky header -->
        <div class="sticky top-0 z-10 bg-white border-b border-gray-100">
          <div class="flex items-center justify-between px-4 h-14">
            <h1 class="text-[22px] font-bold text-gray-900 tracking-tight leading-none">Availability</h1>
            <button @click="updateAvailability" :disabled="saving" type="button"
              class="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full disabled:opacity-60 active:opacity-80 transition-opacity">
              <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>

        <!-- Scrollable content -->
        <div class="pb-20">

          <!-- ── Section: Weekly schedule ── -->
          <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Weekly schedule</p>
          <div class="bg-white rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100">
            <div v-for="day in DAYS" :key="day.key"
              class="flex items-center px-4 py-3.5 gap-3 active:bg-gray-50 transition-colors">

              <!-- Day name -->
              <span class="text-[15px] font-medium text-gray-900 w-28 flex-shrink-0">{{ day.label }}</span>

              <!-- Hours or unavailable -->
              <span class="flex-1 text-[13px] truncate transition-colors"
                :class="calendar[day.key] ? 'text-gray-400' : 'text-gray-300'">
                {{ calendar[day.key]
                  ? formattedHour(calendar.start_at_hour, user.time_format) + ' – ' + formattedHour(calendar.end_at_hour, user.time_format)
                  : 'Unavailable' }}
              </span>

              <!-- iOS-style toggle -->
              <button type="button"
                @click="calendar[day.key] = !calendar[day.key]"
                class="relative flex-shrink-0 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none"
                :class="calendar[day.key] ? 'bg-blue-600' : 'bg-gray-200'">
                <span class="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200"
                  :class="calendar[day.key] ? 'translate-x-5' : 'translate-x-0'" />
              </button>
            </div>
          </div>

          <!-- ── Section: Default hours ── -->
          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Default hours</p>
          <div class="bg-white rounded-2xl mx-4 px-4 py-3.5 flex items-center gap-3">
            <svg class="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
            </svg>
            <select v-model="calendar.start_at_hour"
              class="flex-1 py-2 px-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
              <option v-for="hour in total_hours" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
            </select>
            <span class="text-gray-400 text-sm font-medium flex-shrink-0">–</span>
            <select v-model="calendar.end_at_hour"
              class="flex-1 py-2 px-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
              <option v-for="hour in toHours(calendar.start_at_hour)" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
            </select>
          </div>

          <!-- ── Section: Date overrides ── -->
          <p class="px-4 pt-5 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Date overrides</p>
          <div class="bg-white rounded-2xl mx-4 overflow-hidden">
            <p class="px-4 pt-3 text-xs text-gray-400">Tap a date to set custom hours for that day</p>
            <v-date-picker
              :locale="CURRENT_LOCALE"
              :modelValue="false"
              class="availability-calendar-compact w-full"
              :attributes="attributes"
              :is-dark="isDark"
              :first-day-of-week="1"
              :min-date="new Date()"
              :max-date="DaysFromNowOn(diableAllDatesAfterXDays)"
              @update:from-page="calendarPageChange"
              is-expanded>
              <template v-slot:day-content="{ day, attributes: dayAttrs }">
                <div class="flex flex-col items-center justify-center h-full select-none"
                  :class="getHolidayAttr(dayAttrs) || hasCustomizableAvailability(day) ? 'cursor-pointer' : 'cursor-default'"
                  @click="handleDayClick(day)">

                  <!-- Day number circle -->
                  <span class="w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors"
                    :class="isToday(day) && !getHolidayAttr(dayAttrs)
                      ? 'bg-blue-600 text-white font-semibold'
                      : hasCustomizableAvailability(day) && !getHolidayAttr(dayAttrs)
                        ? 'text-gray-800'
                        : 'text-gray-400'"
                    :style="getHolidayAttr(dayAttrs) ? { backgroundColor: holidayColor(getHolidayAttr(dayAttrs)), color: '#fff' } : {}">
                    {{ day.day }}
                  </span>

                  <!-- Dot indicator -->
                  <span v-if="!getHolidayAttr(dayAttrs) && (dayAttrs ?? []).length > 0"
                    class="w-1.5 h-1.5 rounded-full bg-blue-500 mt-0.5 flex-shrink-0"></span>
                  <span v-else class="w-1.5 h-1.5 mt-0.5 flex-shrink-0"></span>
                </div>
              </template>
            </v-date-picker>
          </div>

        </div>
      </div>

      <!-- ── DESKTOP LAYOUT — fills viewport, no page scroll ── -->
      <div class="hidden sm:flex sm:flex-col sm:h-screen">

        <!-- Slim page header -->
        <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between px-6 py-3.5">
            <div>
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Availability</h1>
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Set working hours and date overrides</p>
            </div>
            <button @click="updateAvailability" :disabled="saving" type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors shadow-sm flex-shrink-0">
              <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
              </svg>
              {{ saving ? 'Saving...' : 'Save changes' }}
            </button>
          </div>
        </div>

        <!-- Body: left panel + calendar — fills remaining height, nothing overflows the page -->
        <div class="flex-1 flex overflow-hidden">

          <!-- Left panel: working hours -->
          <div class="w-72 xl:w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-y-auto flex flex-col">

            <!-- Days -->
            <div class="px-5 pt-6 pb-5 border-b border-gray-100 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Available days</p>
              <div class="grid grid-cols-7 gap-1">
                <button v-for="day in DAYS" :key="day.key"
                  @click="calendar[day.key] = !calendar[day.key]"
                  type="button"
                  class="flex flex-col items-center py-2.5 rounded-lg text-xs font-semibold transition-all"
                  :class="calendar[day.key]
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'">
                  {{ day.short }}
                </button>
              </div>
            </div>

            <!-- Hours -->
            <div class="px-5 py-5">
              <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Working hours</p>
              <div class="flex items-center gap-2">
                <select v-model="calendar.start_at_hour"
                  class="flex-1 py-2 px-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-[#22272E] text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                  <option v-for="hour in total_hours" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                </select>
                <span class="text-gray-400 dark:text-gray-500 text-sm font-medium flex-shrink-0">to</span>
                <select v-model="calendar.end_at_hour"
                  class="flex-1 py-2 px-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-[#22272E] text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer">
                  <option v-for="hour in toHours(calendar.start_at_hour)" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                </select>
              </div>
            </div>

            <!-- Spacer pushes nothing but lets panel scroll if needed -->
            <div class="flex-1"></div>
          </div>

          <!-- Right panel: calendar fills all remaining space, scrolls internally only -->
          <div class="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#0d1117]">
            <v-date-picker
              :locale="CURRENT_LOCALE"
              :modelValue="false"
              class="availability-calendar max-w-full"
              :attributes="attributes"
              :is-dark="isDark"
              :first-day-of-week="1"
              :min-date="new Date()"
              :max-date="DaysFromNowOn(diableAllDatesAfterXDays)"
              @update:from-page="calendarPageChange"
              is-expanded>
              <template v-slot:day-content="{ day, attributes: dayAttrs }">
                <!-- Holiday: full-cell color — clickable to edit/remove -->
                <div v-if="getHolidayAttr(dayAttrs)"
                  class="flex flex-col h-full p-2 select-none cursor-pointer hover:brightness-90 transition-all"
                  :class="holidayColorClass(getHolidayAttr(dayAttrs))"
                  @click="handleDayClick(day)">
                  <span class="text-xs font-semibold text-white/60 leading-none mb-1.5">{{ day.day }}</span>
                  <p class="text-[11px] font-bold text-white leading-snug line-clamp-3">
                    {{ holidayTitle(getHolidayAttr(dayAttrs)) }}
                  </p>
                </div>
                <!-- Normal day -->
                <div v-else
                  class="flex flex-col h-full p-2 select-none"
                  :class="hasCustomizableAvailability(day) ? 'vc-availableday cursor-pointer' : 'cursor-default'"
                  @click="handleDayClick(day)">
                  <span class="inline-flex items-center justify-center w-7 h-7 mb-1.5 text-sm font-bold rounded-full flex-shrink-0"
                    :class="isToday(day)
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 dark:text-gray-200'">
                    {{ day.day }}
                  </span>
                  <div class="flex flex-col gap-0.5 overflow-hidden">
                    <div v-for="attr in (dayAttrs ?? [])" :key="attr.key"
                      class="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight truncate bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      <span class="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></span>
                      <span class="truncate">{{ attr.customData.title }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>

        </div>

      </div>
    </template>

    <!-- ── Day override modal ── -->
    <Transition name="modal">
      <div v-if="dayModal.open"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="dayModal.open = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-md w-full p-6 animate-in">

          <!-- Modal header -->
          <div class="flex items-start justify-between mb-4">
            <div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white">{{ selectedDayName }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {{ activeTab === 'hours' ? 'Set custom hours for this day' : 'Mark this day as a holiday' }}
              </p>
            </div>
            <button @click="dayModal.open = false" type="button"
              class="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0 mt-0.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Tab toggle -->
          <div class="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-1 mb-5">
            <button type="button" @click="activeTab = 'hours'"
              class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              :class="activeTab === 'hours'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
              </svg>
              Set hours
            </button>
            <button type="button" @click="activeTab = 'holiday'"
              class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
              :class="activeTab === 'holiday'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
              </svg>
              Mark as holiday
            </button>
          </div>

          <!-- ── TAB: Set hours ── -->
          <div v-if="activeTab === 'hours'" class="space-y-3 mb-5">
            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Hours available</p>

            <!-- No slots -->
            <div v-if="selectedDateAvailabilities.length === 0" class="flex items-center gap-3">
              <div class="flex-1 py-3 border border-dashed border-gray-200 dark:border-gray-600 rounded-xl text-center text-sm text-gray-400 dark:text-gray-500">
                Unavailable
              </div>
              <button @click="addSlotInModal" type="button"
                class="w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors flex-shrink-0">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                </svg>
              </button>
            </div>

            <!-- Slot rows -->
            <div v-else class="space-y-2">
              <div v-for="(availability, index) in selectedDateAvailabilities" :key="index" class="space-y-1">
                <div class="flex items-center gap-2">
                  <select v-model="availability.customData.start_at_24_hour"
                    class="flex-1 py-2 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-[#22272E] text-gray-900 dark:text-gray-200 cursor-pointer"
                    :class="validateIfSlotAlreadyExists(index) ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'">
                    <option v-for="hour in total_hours" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                  </select>
                  <span class="text-gray-400 text-sm flex-shrink-0">to</span>
                  <select v-model="availability.customData.end_at_24_hour"
                    class="flex-1 py-2 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-[#22272E] text-gray-900 dark:text-gray-200 cursor-pointer"
                    :class="validateIfSlotAlreadyExists(index) ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600'">
                    <option v-for="hour in toHours(availability.customData.start_at_24_hour)" :key="hour" :value="hour">{{ formattedHour(hour, user.time_format) }}</option>
                  </select>
                  <button @click="removeSlotFromModal(index)" type="button"
                    class="w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-400 hover:text-red-500 hover:border-red-300 transition-colors flex-shrink-0">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                    </svg>
                  </button>
                  <button v-if="index === 0" @click="addSlotInModal" type="button"
                    class="w-9 h-9 rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors flex-shrink-0">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                    </svg>
                  </button>
                </div>
                <p v-if="validateIfSlotAlreadyExists(index)" class="text-xs text-red-500 dark:text-red-400">
                  This slot overlaps with another
                </p>
              </div>
            </div>

            <!-- Apply to all Xs -->
            <label class="flex items-center gap-2.5 pt-1 cursor-pointer select-none">
              <input type="checkbox" v-model="applyToAllDays"
                class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500">
              <span class="text-sm text-gray-700 dark:text-gray-300">
                Apply to all {{ selectedDayName }}s
              </span>
            </label>
          </div>

          <!-- ── TAB: Mark as holiday ── -->
          <div v-else class="space-y-4 mb-5">

            <!-- Name -->
            <div>
              <label class="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Holiday name</label>
              <input v-model="holidayForm.name" type="text" required
                placeholder="e.g. Christmas Day"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-[#22272E] text-gray-900 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow" />
            </div>

            <!-- Date range -->
            <div>
              <label class="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Date range</label>
              <v-date-picker
                v-model="holidayTargetDate"
                :key="holidayFormKey"
                :locale="CURRENT_LOCALE"
                :popover="{ visibility: 'focus' }"
                mode="date"
                is-range
                :is-dark="isDark">
                <template #default="{ inputValue, inputEvents }">
                  <div class="relative">
                    <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    <input type="text" readonly
                      :value="inputValue.start === inputValue.end ? inputValue.start : inputValue.start + ' – ' + inputValue.end"
                      v-on="inputEvents.start"
                      class="w-full pl-10 pr-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-[#22272E] text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                  </div>
                </template>
              </v-date-picker>
            </div>

            <!-- Color picker -->
            <div>
              <label class="block text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5">Color</label>
              <div class="flex flex-wrap gap-2">
                <button v-for="color in HOLIDAY_COLORS" :key="color.name"
                  type="button"
                  @click="holidayForm.color = color.name"
                  class="w-8 h-8 rounded-full transition-all relative flex-shrink-0"
                  :style="{ backgroundColor: color.hex }"
                  :class="holidayForm.color === color.name
                    ? 'ring-2 ring-offset-2 ring-blue-500 scale-110'
                    : 'hover:scale-105'"
                  :title="color.label">
                  <svg v-if="holidayForm.color === color.name"
                    class="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow-sm"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 0 1 0 1.414l-8 8a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 1.414-1.414L8 12.586l7.293-7.293a1 1 0 0 1 1.414 0Z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Modal footer -->
          <div class="flex gap-3">
            <button @click="dayModal.open = false" type="button"
              class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>

            <!-- Hours tab: Apply button -->
            <button v-if="activeTab === 'hours'"
              @click="saveFromModal" type="button"
              :disabled="disableDailyAvailabilitySubmitBtn || saving"
              class="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
              <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
              {{ saving ? 'Saving...' : 'Apply' }}
            </button>

            <!-- Holiday tab: create / update / remove -->
            <template v-else>
              <!-- Editing existing holiday: Remove + Update -->
              <template v-if="editingHoliday">
                <button @click="removeHoliday" type="button"
                  :disabled="removingHoliday || savingHoliday"
                  class="px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 transition-colors flex items-center gap-1.5 flex-shrink-0">
                  <svg v-if="removingHoliday" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  <svg v-else class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                  </svg>
                  {{ removingHoliday ? 'Removing...' : 'Remove' }}
                </button>
                <button @click="saveHoliday" type="button"
                  :disabled="!holidayForm.name.trim() || savingHoliday || removingHoliday"
                  class="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
                  <svg v-if="savingHoliday" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  {{ savingHoliday ? 'Updating...' : 'Update holiday' }}
                </button>
              </template>
              <!-- New holiday: Mark as holiday -->
              <button v-else
                @click="saveHoliday" type="button"
                :disabled="!holidayForm.name.trim() || savingHoliday"
                class="flex-1 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
                <svg v-if="savingHoliday" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                {{ savingHoliday ? 'Saving...' : 'Mark as holiday' }}
              </button>
            </template>
          </div>

        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRequest } from 'composables/useRequest.js'
import { useGlobalStore } from 'stores/global.js'
import { useAvailabilityStore } from 'stores/availability.js'
import { useHolidaysStore } from 'stores/holidays.js'
import { useConstants } from 'composables/useConstants.js'
import { useFormatting } from 'composables/useFormatting.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const globalStore = useGlobalStore()
const availabilityStore = useAvailabilityStore()
const holidaysStore = useHolidaysStore()
const { I18N, CURRENT_LOCALE } = useConstants()

const HOLIDAY_COLORS = [
  { name: 'tomato',    hex: '#d50000', label: 'Tomato' },
  { name: 'flamingo',  hex: '#e67c73', label: 'Flamingo' },
  { name: 'tangerine', hex: '#f4511e', label: 'Tangerine' },
  { name: 'banana',    hex: '#f6bf26', label: 'Banana' },
  { name: 'sage',      hex: '#33b679', label: 'Sage' },
  { name: 'basil',     hex: '#0b8043', label: 'Basil' },
  { name: 'peacock',   hex: '#039be5', label: 'Peacock' },
  { name: 'blueberry', hex: '#3f51b5', label: 'Blueberry' },
  { name: 'lavender',  hex: '#7986cb', label: 'Lavender' },
  { name: 'grape',     hex: '#8e24aa', label: 'Grape' },
  { name: 'graphite',  hex: '#616161', label: 'Graphite' },
  { name: 'silver',    hex: '#b0b0b0', label: 'Silver' },
]
const {
  total_hours,
  toHours,
  formattedHour,
  diableAllDatesAfterXDays,
  DaysFromNowOn,
  beginningOfMonthFromMonthNumber,
} = useFormatting()

const DAYS = [
  { key: 'sunday',    short: 'Sun', label: 'Sunday' },
  { key: 'monday',    short: 'Mon', label: 'Monday' },
  { key: 'tuesday',   short: 'Tue', label: 'Tuesday' },
  { key: 'wednesday', short: 'Wed', label: 'Wednesday' },
  { key: 'thursday',  short: 'Thu', label: 'Thursday' },
  { key: 'friday',    short: 'Fri', label: 'Friday' },
  { key: 'saturday',  short: 'Sat', label: 'Saturday' },
]

// ── State ──
const saving = ref(false)
const calendar = ref({})

// Derive loading/error from the store so the skeleton shows on first load
// but not on cached return visits
const loading = computed(() => availabilityStore.loading)
const error = computed(() => availabilityStore.error)
const calendarAvailabilities = ref({})
const attributes = ref([])
const applyToAllDays = ref(false)
const customizeableDays = ref([])
const defaultSlot = ref({})
const selectedDateForCustomization = ref({ day: null })
const selectedDateAvailabilities = ref([])
const currentCalendarMonth = ref({})
const isDark = ref(document.documentElement.classList.contains('dark'))
const dayModal = ref({ open: false })

// ── Modal tab state ──
const activeTab = ref('hours')   // 'hours' | 'holiday'
const holidayForm = ref({ name: '', color: 'peacock' })
const holidayTargetDate = ref({ start: new Date(), end: new Date() })
const holidayFormKey = ref(0)
const savingHoliday = ref(false)
const removingHoliday = ref(false)
const editingHoliday = ref(null)   // null = creating new, object = editing existing

const user = computed(() => globalStore.user || {})

// ── Computed ──
const selectedDayName = computed(() => {
  const day = selectedDateForCustomization.value.day
  if (!day?.ariaLabel) return ''
  return day.ariaLabel.split(',')[0]
})

const selectedDate = computed(() => {
  const day = selectedDateForCustomization.value.day
  return day?.id || ''
})

const disableDailyAvailabilitySubmitBtn = computed(() => {
  const slots = selectedDateAvailabilities.value
  for (let i = 0; i < slots.length; i++) {
    const start = slots[i].customData.start_at_24_hour
    const end = slots[i].customData.end_at_24_hour
    if (start >= end || start >= 23) return true
    for (let j = 0; j < slots.length; j++) {
      if (i !== j) {
        const sStart = slots[j].customData.start_at_24_hour
        const sEnd = slots[j].customData.end_at_24_hour
        if (start >= sStart && start < sEnd) return true
      }
    }
  }
  return false
})

// ── Lifecycle ──
onMounted(async () => {
  await globalStore.fetchCurrentUser()
  // Fetch calendar data and holidays in parallel — holidays are needed to
  // detect and edit holiday cells when the user clicks them.
  await Promise.all([fetchCalendarData(), holidaysStore.fetchIfStale()])
})

// ── Data loading ──
async function fetchCalendarData() {
  await availabilityStore.fetchIfStale()
  if (availabilityStore.calendar) {
    // Shallow-clone so local edits (day toggles, hours) don't mutate the store
    calendar.value = { ...availabilityStore.calendar }
    await fetchCalendarAvailabilities()
  }
}

async function fetchCalendarAvailabilities() {
  if (!calendar.value.id) return
  const month = currentCalendarMonth.value
  const startDate = month.month
    ? beginningOfMonthFromMonthNumber(month.month, month.year)
    : new Date().toISOString().slice(0, 10)
  const res = await axiosRequest('GET', `/calendars/${calendar.value.id}?start_date=${startDate}`, {}, false)
  if (!res.js_error && res.response.success) {
    calendarAvailabilities.value = res.response.data
    customizeableDays.value = res.response.data.customizeable_dates
    attributes.value = res.response.data.data
    defaultSlot.value = res.response.data.default_object
  }
}

// ── Calendar interaction ──
function calendarPageChange(event) {
  currentCalendarMonth.value = event
  fetchCalendarAvailabilities()
}

function handleDayClick(day) {
  selectedDateForCustomization.value.day = day

  // Check if this date falls within an existing holiday — open the holiday tab pre-filled
  const existingHoliday = holidaysStore.holidays.find(h => {
    const start = (h.start_at || '').slice(0, 10)
    const end = (h.end_at || '').slice(0, 10)
    return day.id >= start && day.id <= end
  })

  if (existingHoliday) {
    editingHoliday.value = existingHoliday
    activeTab.value = 'holiday'
    holidayForm.value = { name: existingHoliday.name, color: existingHoliday.color }
    const [ys, ms, ds] = existingHoliday.start_at.slice(0, 10).split('-').map(Number)
    const [ye, me, de] = existingHoliday.end_at.slice(0, 10).split('-').map(Number)
    holidayTargetDate.value = { start: new Date(ys, ms - 1, ds), end: new Date(ye, me - 1, de) }
    holidayFormKey.value++
    dayModal.value.open = true
    return
  }

  // Not a holiday — only allow clicking on customizable (available) days
  if (!customizeableDays.value.includes(day.id)) return
  editingHoliday.value = null
  applyToAllDays.value = false
  selectedDateAvailabilities.value = (calendarAvailabilities.value.data || [])
    .filter(obj => obj.dates.includes(day.id))
  activeTab.value = 'hours'
  const [y, m, d] = day.id.split('-').map(Number)
  const clickedDate = new Date(y, m - 1, d)
  holidayForm.value = { name: day.ariaLabel?.split(',')[0] || '', color: 'peacock' }
  holidayTargetDate.value = { start: clickedDate, end: clickedDate }
  holidayFormKey.value++
  dayModal.value.open = true
}

function hasCustomizableAvailability(day) {
  return customizeableDays.value.includes(day.id)
}

function isToday(day) {
  return day.date.toDateString() === new Date().toDateString()
}

const HOLIDAY_COLOR_NAMES = ['tomato','flamingo','tangerine','banana','sage','basil','peacock','blueberry','lavender','grape','graphite','silver']

const HOLIDAY_COLOR_MAP = {
  tomato:     '#d50000',
  flamingo:   '#e67c73',
  tangerine:  '#f4511e',
  banana:     '#f6bf26',
  sage:       '#33b679',
  basil:      '#0b8043',
  peacock:    '#039be5',
  blueberry:  '#3f51b5',
  lavender:   '#7986cb',
  grape:      '#8e24aa',
  graphite:   '#616161',
  silver:     '#b0b0b0',
}

function isHoliday(attr) {
  return !!attr.customData.class?.includes('text-white')
}

function getHolidayAttr(dayAttrs) {
  return (dayAttrs ?? []).find(isHoliday) ?? null
}

function holidayColorClass(attr) {
  const found = attr.customData.class?.split(' ').find(c => HOLIDAY_COLOR_NAMES.includes(c))
  return found || 'basil'
}

function holidayTitle(attr) {
  return (attr.customData.title || '').replace(/<br\s*\/?>/gi, ' ').replace(/\(Holiday\)/i, '').trim()
}

function holidayColor(attr) {
  const cls = attr.customData.class?.split(' ').find(c => HOLIDAY_COLOR_NAMES.includes(c))
  return HOLIDAY_COLOR_MAP[cls] || '#616161'
}

// ── Slot management ──
function validateIfSlotAlreadyExists(index) {
  const slots = selectedDateAvailabilities.value
  const start = slots[index].customData.start_at_24_hour
  for (let i = 0; i < slots.length; i++) {
    if (i !== index) {
      const sStart = slots[i].customData.start_at_24_hour
      const sEnd = slots[i].customData.end_at_24_hour
      if (start >= sStart && start < sEnd) return true
    }
  }
  return false
}

function addSlotInModal() {
  if (selectedDateAvailabilities.value.length === 0) {
    const slot = JSON.parse(JSON.stringify(defaultSlot.value))
    slot.dates = [selectedDate.value]
    selectedDateAvailabilities.value.push(slot)
  } else {
    const last = selectedDateAvailabilities.value[selectedDateAvailabilities.value.length - 1]
    const clone = JSON.parse(JSON.stringify(last))
    clone.customData.start_at_24_hour = clone.customData.end_at_24_hour
    clone.customData.end_at_24_hour = Math.min(clone.customData.end_at_24_hour + 1, 23)
    selectedDateAvailabilities.value.push(clone)
  }
}

function removeSlotFromModal(index) {
  selectedDateAvailabilities.value.splice(index, 1)
}

// ── Save actions ──
async function saveFromModal() {
  saving.value = true
  const res = await axiosRequest('PUT', `/calendars/${calendar.value.id}`, {
    calendar: calendar.value,
    selected_date_availabilities: selectedDateAvailabilities.value,
    apply_to_all_days: applyToAllDays.value,
    target_date: selectedDate.value,
  }, false)
  if (!res.js_error && res.response.success) {
    calendarAvailabilities.value = res.response.data
    customizeableDays.value = res.response.data.customizeable_dates
    attributes.value = res.response.data.data
    defaultSlot.value = res.response.data.default_object
    availabilityStore.updateCalendar(calendar.value)
    await fetchCalendarAvailabilities()
    dayModal.value.open = false
    flash.value?.show('Availability updated', 'notice')
  } else {
    flash.value?.show('Failed to update availability', 'alert')
  }
  saving.value = false
}

function toLocalDateStr(date) {
  const d = new Date(date)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

async function saveHoliday() {
  if (!holidayForm.value.name.trim()) return
  savingHoliday.value = true
  const isEdit = !!editingHoliday.value
  const url = isEdit ? `/holidays/${editingHoliday.value.id}` : '/holidays'
  const method = isEdit ? 'PUT' : 'POST'
  const res = await axiosRequest(method, url, {
    holiday: {
      name: holidayForm.value.name,
      color: holidayForm.value.color,
      start_at: toLocalDateStr(holidayTargetDate.value.start),
      end_at: toLocalDateStr(holidayTargetDate.value.end),
    },
  }, false)
  if (!res.js_error && res.response.success) {
    holidaysStore.setFromMutationResponse(res.response.holidays)
    dayModal.value.open = false
    await fetchCalendarAvailabilities()
    flash.value?.show(res.response.message || (isEdit ? 'Holiday updated' : 'Holiday added'), 'notice')
  } else {
    flash.value?.show(res.response?.error || 'Failed to save holiday', 'alert')
  }
  savingHoliday.value = false
}

async function removeHoliday() {
  if (!editingHoliday.value) return
  removingHoliday.value = true
  const res = await axiosRequest('DELETE', `/holidays/${editingHoliday.value.id}`, {}, false)
  if (!res.js_error && res.response.success) {
    holidaysStore.setFromMutationResponse(res.response.holidays)
    dayModal.value.open = false
    await fetchCalendarAvailabilities()
    flash.value?.show(res.response.message || 'Holiday removed', 'notice')
  } else {
    flash.value?.show('Failed to remove holiday', 'alert')
  }
  removingHoliday.value = false
}

async function updateAvailability() {
  saving.value = true
  const res = await axiosRequest('PUT', `/calendars/${calendar.value.id}`, {
    calendar: calendar.value,
    selected_date_availabilities: [],
    apply_to_all_days: false,
  }, false)
  if (!res.js_error && res.response.success) {
    calendarAvailabilities.value = res.response.data
    customizeableDays.value = res.response.data.customizeable_dates
    attributes.value = res.response.data.data
    defaultSlot.value = res.response.data.default_object
    availabilityStore.updateCalendar(calendar.value)
    flash.value?.show('Availability saved', 'notice')
  } else {
    flash.value?.show('Failed to save availability', 'alert')
  }
  saving.value = false
}
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }

.vc-availableday { background-color: #eff6ff; }
:global(.dark) .vc-availableday { background-color: rgba(29, 78, 216, 0.1); }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
